<template>
  <div>
    <div
      v-if="apparatus"
      class="card apparatus-card"
      :class="{ hoverPointer: isClickable }"
      @click="openErrorModal()"
      :header-bg-variant="getHeaderBGVariant()"
      header-class="dashboard-card-header"
      data-test-hook="apparatus-clickable-card-apparatus-card"
    >
      <!--Header-->
      <div
        v-if="
          !generatorEnrollmentComplete() || ((isInGracePeriod || generatorIsDunning()) && !apparatus.isDealerManaged)
        "
      >
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="DisconnectedHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong v-if="generatorIsDunning() || isInGracePeriod" data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xUpdateYourPaymentMethod') }}
              </strong>
              <strong v-else data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xFinishSettingUpYourProduct') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="generatorDealerSubscriptionExpired()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="NoSubscriptionHeader" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourDealerManagedSubscriptionHasExpired') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="hsbHasDealerAndActivePremiumSub()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="GeneratorInDealerPlanAndPremiumPlan" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourAccountNeedsAttentionHeader') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="basicHSBWithExistingSubscription()">
        <div class="card-header dashboard-card-header bg-statusWarning">
          <div data-test-hook="NotCoveredGenerator" class="row w-100 no-gutters">
            <div class="float-left col">
              <strong data-test-hook="HeaderText" class="py-0 my-0">
                {{ $t('xYourAccountNeedsAttentionHeader') }}
              </strong>
            </div>
            <div class="float-right mr-0 pr-0 col-auto">
              <i class="fa-solid fa-arrow-right" />
            </div>
          </div>
        </div>
      </div>
      <div class="yellowstone-card-body">
        <!--Hero Image and Address-->
        <div class="row d-flex">
          <div class="col-4">
            <img :src="apparatus.heroImageUrl" data-test-hook="ApparatusHeroImg" alt="Hero Image" class="img-fluid" />
          </div>
          <div class="pt-4 col-8">
            <div class="row">
              <div data-test-hook="ApparatusAddress" class="ellipsis-overflow mb-2 col">
                {{ apparatus.localizedAddress }}
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h2 data-test-hook="ApparatusName" class="pb-1 mb-0">
                  {{ apparatus.name }}
                </h2>
              </div>
            </div>
          </div>
        </div>
        <b-alert show class="alert alert-info px-3 pt-3 pb-3">
          <div class="d-flex align-items-start">
            <div class="flex-shrink-0">
              <i class="far fa-circle-exclamation"></i>
            </div>

            <div class="ml-2" style="font-weight: normal;">
              {{ $t('xDownloadAppToMonitorText') }}
              <MobileDownloadLinkButtons />
            </div>
          </div>
        </b-alert>
      </div>
    </div>

    <!--Basic gen with existing active plan-->
    <div
      v-if="apparatus && apparatus.apparatusId"
      :id="'basic-gen-with-existing-subscription' + apparatus.apparatusId"
      role="dialog"
      class="modal fade show"
    >
      <div class="modal-dialog modal-dialog-centered">
        <span tabindex="0"></span>
        <div tabindex="-1" class="modal-content">
          <header class="modal-header">
            <div class="row w-100 no-gutters align-items-center">
              <div class="col">
                <h2 class="modal-title">
                  <i class="far fa-exclamation-triangle colorOrange mr-1" />
                  {{ $t('xYourAccountNeedsAttentionHeader') }}
                </h2>
              </div>
              <div class="float-right col-auto">
                <button
                  type="button"
                  class="btn btn-icon-button--gray btn-icon-button"
                  @click="closeBasicGenWithExistingSubscriptionModal()"
                >
                  <i class="fas fa-times" />
                </button>
              </div>
            </div>
          </header>
          <div class="modal-body">
            <div class="row">
              <div class="col">
                {{ $t('xGeneratorNotCoveredUnderPremiumPlan') }}
              </div>
            </div>
          </div>
          <ModalFooter>
            <button
              type="button"
              @click="goToServiceOps()"
              :class="apparatusesOverLimit ? 'btn btn-primary' : 'btn btn-secondary'"
            >
              {{ $t('xFindADealer') }}
            </button>
            <button
              v-if="!apparatusesOverLimit"
              type="button"
              @click="
                closeBasicGenWithExistingSubscriptionModal(),
                  $emit('openHSBEnrollmentFlow', apparatus.apparatusId, apparatus.modelNumber)
              "
              class="btn btn-primary"
            >
              {{ $t('xUpgradeNow') }}
            </button>
          </ModalFooter>
        </div>
      </div>
    </div>

    <!--Dealer Managed Subscription Modal-->
    <div
      v-if="apparatus && apparatus.apparatusId"
      :id="'dealer-managed-sub-expired' + apparatus.apparatusId"
      role="dialog"
      class="modal fade show"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg" style="width: 720px;">
        <span tabindex="0"></span>
        <div tabindex="-1" class="modal-content">
          <header class="modal-header">
            <div class="row w-100 no-gutters align-items-center">
              <div class="col">
                <h2 class="modal-title">
                  <i class="far fa-exclamation-triangle colorOrange mr-1" />
                  {{ $t('xYourDealerManagedSubscriptionHasExpired') }}
                </h2>
              </div>
              <div class="float-right col-auto">
                <button type="button" class="btn btn-icon-button--gray btn-icon-button" @click="closeDealerModal()">
                  <i class="fas fa-times" />
                </button>
              </div>
            </div>
          </header>
          <div class="modal-body">
            <div class="row">
              <div v-if="preferredDealer" class="col">
                <p class="mb-4">
                  {{ $t('xContactYourDealer') }}
                </p>
                <p v-if="preferredDealer.name" data-test-hook="DealerAddress1">
                  <strong>{{ preferredDealer.name }}</strong>
                </p>
                <p v-if="preferredDealer.topLineAddress" data-test-hook="DealerAddress1">
                  {{ preferredDealer.topLineAddress }}
                </p>
                <p v-if="preferredDealer.bottomLineAddress" data-test-hook="DealerAddress2">
                  {{ preferredDealer.bottomLineAddress }}
                </p>
                <p v-if="preferredDealer.phoneNumber" data-test-hook="DealerPhoneNumber">
                  {{ preferredDealer.phoneNumber | phone }}
                </p>
                <a v-if="preferredDealer.email" :href="'mailto:' + preferredDealer.email">
                  {{ preferredDealer.email }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--Update Payment Modal-->
    <UpdateCreditCardModal
      v-if="generatorIsDunning() || isInGracePeriod"
      :existingPaymentInfo="paymentInfo"
      :accountProfile="accountProfile"
      :apparatusId="apparatusInfo.apparatusId"
      :subscriptionStatus="
        this.subscription && this.subscription[0] && this.subscription[0].value && this.subscription[0].value.status
          ? this.subscription[0].value.status
          : null
      "
    />

    <MultipleChargesModal
      v-if="apparatus && hsbSubscriptionDetails"
      :apparatus="apparatus"
      :currentPremiumSubscription="hsbSubscriptionDetails"
    />

    <MobileSubscriptionModal
      v-if="apparatus"
      :modalId="`mobile-sub-modal-${apparatus.apparatusId}`"
      :store="mobileSubscriptionStore"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import UpdateCreditCardModal from '@/components/Dashboard/UpdateCreditCardModal'
import MultipleChargesModal from './MultipleChargesModal.vue'
import * as Constants from '@/constants'
import { getSubscriptionForApparatus, getLegacySubscriptionForApparatus } from '@/utils/mixins'
import MobileSubscriptionModal from '../Enrollment/Subscription/MobileSubscriptionModal.vue'
import MobileDownloadLinkButtons from '../Common/MobileDownloadLinkButtons.vue'

export default {
  components: { UpdateCreditCardModal, MultipleChargesModal, MobileSubscriptionModal, MobileDownloadLinkButtons },
  props: ['apparatuses', 'apparatusInfo', 'paymentInfo', 'organizationSubscriptionDetailsData'],
  data() {
    return {
      device: null,
      subscription: null,
      startSubFlow: false,
      apparatus: null,
      FreeSub: Constants.FreeSubscription,
      NoSub: Constants.NoSubscription,
      temperature: 0,
      temperatureUnits: 'F',
      wifi: Constants.HsbWifiDeviceType,
      organizationSubscriptionDetails: null,
      hsbSubscriptionDetails: null,
      hsbLegacySubscriptionDetails: null,
      apparatusesOverLimit: false,
      mobileSubscriptionStore: null,
      preferredDealer: null
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      user: state => state.account.user,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    billingMigrationFeatureFlag() {
      return Constants.BillingMigrationFeatureFlag
    },
    isInGracePeriod() {
      return this.hsbSubscriptionDetails?.reason == Constants.SubscriptionDetailsReasonGracePeriodStarted
    },
    isClickable() {
      // For determining if we show the pointer cursor when hovering
      // these should match up to the conditions that lead to a modal opening in this.openErrorModal()
      const isEnrollmentUnfinished = !this.generatorEnrollmentComplete()
      const needsSubscription = this.generatorDealerSubscriptionExpired() || this.basicHSBWithExistingSubscription()
      const hasDuplicateSub = this.hsbHasDealerAndActivePremiumSub()
      const isDunningOrGracePeriod = this.generatorIsDunning() || this.isInGracePeriod

      return isEnrollmentUnfinished || needsSubscription || hasDuplicateSub || isDunningOrGracePeriod
    }
  },
  methods: {
    getHeaderBGVariant() {
      if (!this.generatorEnrollmentComplete()) return 'statusWarning'
      else if (this.warningHeader) return 'statusWarning'
    },
    openEnrollmentFlow() {
      if (
        this.currentAppConfig &&
        this.currentAppConfig.featureFlags &&
        !this.currentAppConfig.featureFlags[this.billingMigrationFeatureFlag]
      ) {
        this.$emit('incompleteApparatus', {
          id: this.apparatus.apparatusId,
          device: this.device,
          subscription: this.subscription,
          modelNumber: this.apparatus.modelNumber
        })
      }
    },
    generatorEnrollmentComplete() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }
      if (!this.apparatus.properties) return false
      if (this.device && this.device.length >= 1) {
        return true
      }
      return false
    },
    isBasicHSB() {
      // if it's dealer managed, it's not basic
      if (this.apparatus.isDealerManaged) {
        return false
      }

      // if there's no premium OR legacy subscription at all, it's definitely basic
      if (!this.hsbSubscriptionDetails && !this.hsbLegacySubscriptionDetails) {
        return true
      }

      // if subscription exists, check if cancelled. If there's no subscription, treat it as cancelled
      const isPremiumSubscriptionCancelled = !!this.hsbSubscriptionDetails
        ? this.hsbSubscriptionDetails.status === Constants.SubscriptionDetailCanceled
        : true
      const isLegacySubscriptionCancelled = !!this.hsbLegacySubscriptionDetails
        ? this.hsbLegacySubscriptionDetails.status === Constants.SubscriptionCancelled
        : true

      // if all existing subscriptions are cancelled, it's basic
      if (isPremiumSubscriptionCancelled && isLegacySubscriptionCancelled) {
        return true
      }

      return false
    },
    doesAccountHaveAnExistingSubscription() {
      return (
        //if the account has a premium subscription that isn't canceled, then there is an existing subscription
        this.organizationSubscriptionDetails.subscriptions?.some(
          subscription =>
            subscription.status !== Constants.SubscriptionDetailCanceled &&
            subscription.store !== Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
        ) ||
        //if the account has a legacy subscription that isn't canceled, then there is an existing subscription
        this.organizationSubscriptionDetails.legacyBillingAccount?.products.some(
          product => product.status !== Constants.SubscriptionCancelled
        )
      )
    },
    basicHSBWithExistingSubscription() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      return this.isBasicHSB() && this.doesAccountHaveAnExistingSubscription()
    },
    goToServiceOps() {
      window.open(Constants.RequestDealerUrl)
    },
    openBasicGenWithExistingSubscriptionModal() {
      document.getElementById('basic-gen-with-existing-subscription' + this.apparatus.apparatusId).style.display =
        'block'
    },
    closeBasicGenWithExistingSubscriptionModal() {
      document.getElementById('basic-gen-with-existing-subscription' + this.apparatus.apparatusId).style.display =
        'none'
    },
    closeDealerModal() {
      document.getElementById('dealer-managed-sub-expired' + this.apparatus.apparatusId).style.display = 'none'
    },
    hsbHasDealerAndActivePremiumSub() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      if (
        this.apparatus.isDealerManaged &&
        this.organizationSubscriptionDetails.subscriptions.filter(
          subscription =>
            subscription.apparatuses.some(apparatus => apparatus.apparatusId === this.apparatus.apparatusId) &&
            subscription.status === Constants.SubscriptionDetailActive
        ).length > 1
      ) {
        return true
      }
      return false
    },
    openApparatusWithMultipleSubscriptionsModal() {
      document.getElementById('apparatus-with-multiple-subscriptions' + this.apparatus.apparatusId).style.display =
        'block'
    },
    openHomePremiumApparatusWithMultipleSubscriptionsModal() {
      document.getElementById(
        'home-premium-apparatus-with-multiple-subscriptions' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    openApparatusWithMultipleSubscriptionsMobilePlatformModal() {
      document.getElementById(
        'apparatus-with-multiple-subscriptions-mobile-platform' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal() {
      document.getElementById(
        'remove-apparatus-with-multiple-subscriptions-mobile-platform' + this.apparatus.apparatusId
      ).style.display = 'block'
    },
    generatorDealerSubscriptionExpired() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }

      const hsbDealerSub = this.organizationSubscriptionDetails?.subscriptions.find(
        subscription =>
          subscription.store === Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer') &&
          subscription.apparatuses.find(apparatus => apparatus.apparatusId === this.apparatus.apparatusId)
      )
      // If an HSB sub is cancelled and is a dealer based subscription.
      if (
        this.apparatusInfo?.isDealerManaged &&
        (!hsbDealerSub || hsbDealerSub?.status === Constants.SubscriptionDetailCanceled)
      ) {
        return true
      }

      // If there are no non-legacy subscriptions, turn to legacy billing info if available.
      if (!this.hsbSubscriptionDetails && this.hsbLegacySubscriptionDetails) {
        if (
          this.apparatusInfo &&
          this.apparatusInfo?.isDealerManaged &&
          this.hsbLegacySubscriptionDetails?.status === Constants.SubscriptionCancelled
        ) {
          return true
        }
      }

      return false
    },
    generatorIsDunning() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }
      // Check if it is in grace period before checking anything about apparatus properties. Treat HSBs in grace period as Dunning.
      if (this.isInGracePeriod) return true

      // If an HSB sub is cancelled because the grace period expired, treat the subscription as Basic.
      if (
        this.hsbSubscriptionDetails &&
        this.hsbSubscriptionDetails.status === Constants.SubscriptionDetailCanceled &&
        this.hsbSubscriptionDetails.reason === Constants.SubscriptionDetailsReasonGracePeriodExpired
      ) {
        return false
      }

      // If there are no non-legacy subscriptions, turn to legacy billing info if available.
      if (!this.hsbSubscriptionDetails && this.hsbLegacySubscriptionDetails) {
        if (this.hsbLegacySubscriptionDetails.status === Constants.SubscriptionDunning) {
          return true
        }

        // Treat cancelled HSB subs like Basic.
        if (this.hsbLegacySubscriptionDetails.status === Constants.SubscriptionCancelled) {
          return false
        }
      }

      if (!this.apparatus.properties) return false
      if (this.device && this.subscription && this.device.length >= 1 && this.subscription.length >= 1) {
        if (this.subscription[0].value.status && this.subscription[0].value.isDunning) {
          return true
        }
      }
      return false
    },
    generatorHasDevice() {
      if (!this.apparatus) return false
      if (this.apparatus.type != Constants.HsbApparatusType) {
        return true
      }
      if (!this.apparatus.properties) return false
      if (this.device && this.device.length >= 1) return true
      return false
    },
    openErrorModal() {
      if (this.generatorEnrollmentComplete() && !this.isInGracePeriod && !this.generatorIsDunning()) {
        this.$appInsights.trackEvent({
          name: 'click-apparatus-card',
          properties: { apparatusId: this.apparatus.apparatusId }
        })
        if (this.generatorDealerSubscriptionExpired()) {
          document.getElementById('dealer-managed-sub-expired' + this.apparatus.apparatusId).style.display = 'block'
          this.$appInsights.trackEvent({
            name: 'expired-dealer-managed-sub-hsb',
            properties: {
              page: 'dashboard',
              apparatusId: this.apparatus.apparatusId
            }
          })
        } else if (this.hsbHasDealerAndActivePremiumSub()) {
          this.hsbSubscriptionDetails = this.organizationSubscriptionDetails.subscriptions.find(
            subscription => subscription.store !== Constants.DealerSubscriptionStore
          )
          switch (this.hsbSubscriptionDetails?.store) {
            case Constants.AppleSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsMobilePlatformModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal()
              }
              break
            case Constants.GoogleSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsMobilePlatformModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openRemoveApparatusWithMultipleSubscriptionsMobilePlatformModal()
              }
              break
            case Constants.WebSubscriptionStore:
              if (
                this.isSingleApparatusSubscription(this.hsbSubscriptionDetails?.product?.productId) ||
                this.hsbSubscriptionDetails.apparatuses.length === 1
              ) {
                this.openApparatusWithMultipleSubscriptionsModal()
              } else if (this.isHomePremiumSubscription(this.hsbSubscriptionDetails?.product?.productId)) {
                this.openHomePremiumApparatusWithMultipleSubscriptionsModal()
              }
              break
            default:
              break
          }
          return
        } else if (this.basicHSBWithExistingSubscription()) {
          if (this.accountHasMobileSubscription()) {
            this.$bvModal.show(`mobile-sub-modal-${this.apparatus.apparatusId}`)
            return
          } else {
            this.openBasicGenWithExistingSubscriptionModal()
            return
          }
        }
      } else if (this.generatorIsDunning() || this.isInGracePeriod) {
        if (
          this.currentAppConfig &&
          this.currentAppConfig.featureFlags &&
          !this.currentAppConfig.featureFlags[this.billingMigrationFeatureFlag]
        ) {
          document.getElementById('fixDunningModal' + this.apparatus.apparatusId).style.display = 'block'
        }
      } else {
        this.openEnrollmentFlow()
      }
    },
    closeModal() {
      document.getElementById('dealer-managed-sub-expired' + this.apparatus.apparatusId).style.display = 'none'
    },
    setSubscriptionDetails() {
      // Prefer the use of hsbSubscriptionDetails over hsbLegacySubscriptionDetails if hsbSubscriptionDetails is defined.
      this.hsbSubscriptionDetails = getSubscriptionForApparatus(
        this.apparatus.apparatusId,
        this.organizationSubscriptionDetails.subscriptions
      )
      this.hsbLegacySubscriptionDetails = getLegacySubscriptionForApparatus(
        this.apparatus.apparatusId,
        this.organizationSubscriptionDetails.legacyBillingAccount
      )
    },
    accountHasMobileSubscription() {
      if (!this.subscription) {
        return false
      }

      const activeSubs = this.organizationSubscriptionDetails.subscriptions?.filter(
        sub => sub.status === Constants.SubscriptionActive
      )

      if (!activeSubs.length) {
        return false
      }

      return activeSubs.some(activeSub => {
        if (
          activeSub.store === Constants.AppleSubscriptionStore ||
          activeSub.store === Constants.GoogleSubscriptionStore
        ) {
          this.mobileSubscriptionStore = activeSub.store
          return true
        }

        return false
      })
    }
  },
  watch: {
    apparatusInfo: function(newVal, oldVal) {
      if (newVal) {
        this.apparatus = newVal
        this.device = this.apparatus.properties.filter(x => x.type == Constants.ApparatusPropertyDevice)
        this.subscription = this.apparatus.properties.filter(x => x.type == Constants.ApparatusPropertySubscription)

        this.$store
          .dispatch('getPreferredDealer', this.apparatus.apparatusId)
          .then(response => {
            if (response.status == 204) this.preferredDealer = null
            else {
              this.preferredDealer = response.data
            }
          })
      }
    },
    organizationSubscriptionDetailsData: function(newVal, oldVal) {
      if (newVal) {
        this.organizationSubscriptionDetails = newVal
        this.setSubscriptionDetails()
      }
    }
  },
  mounted() {
    if (this.apparatusInfo) {
      this.apparatus = this.apparatusInfo
      this.device = this.apparatus.properties.filter(x => x.type == Constants.ApparatusPropertyDevice)
      this.subscription = this.apparatus.properties.filter(x => x.type == Constants.ApparatusPropertySubscription)

      this.$store
        .dispatch('getPreferredDealer', this.apparatus.apparatusId)
        .then(response => {
          if (response.status == 204) this.preferredDealer = null
          else {
            this.preferredDealer = response.data
          }
        })
    }
    if (!this.currentAppConfig) this.$store.dispatch('getApplicationConfiguration', 'Any')

    if (this.organizationSubscriptionDetailsData) {
      this.organizationSubscriptionDetails = this.organizationSubscriptionDetailsData
      this.setSubscriptionDetails()
    }

    if (this.apparatuses) {
      let count = 0
      this.apparatuses.forEach(apparatus => {
        if (apparatus.type === Constants.HsbApparatusType && !apparatus.isDealerManaged) {
          count++
        }
      })

      if (count > 2) {
        this.apparatusesOverLimit = true
      }
    }
  }
}
</script>

<style scoped>
.statusIcon {
  font-size: 1.25em;
}
</style>
